import React, { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { FundsRawFiltersProps } from "./types";
import { useDebounce } from "@/hooks/use-debounce";
import { Badge } from "@/components/ui/badge";

interface FilterOption {
  label: string;
  value: string;
  count?: number;
}

// Category options based on actual database values from query, ordered by popularity
const DEFAULT_CATEGORY_OPTIONS: FilterOption[] = [
  // Top 20 most common sa_sub_asset_class values from the database
  { label: "Alternative Strategies", value: "Alternative Strategies" },
  { label: "Neutral Balanced", value: "Neutral Balanced" },
  { label: "Global Equity", value: "Global Equity" },
  { label: "Canadian Equity", value: "Canadian Equity" },
  { label: "Global Fixed Income", value: "Global Fixed Income" },
  { label: "Equity Balanced", value: "Equity Balanced" },
  { label: "U.S. Equity", value: "U.S. Equity" },
  { label: "Canadian Fixed Income", value: "Canadian Fixed Income" },
  { label: "International Equity", value: "International Equity" },
  { label: "Canadian Money Market", value: "Canadian Money Market" },
  { label: "Canadian Focused Equity", value: "Canadian Focused Equity" },
  { label: "Tactical Balanced", value: "Tactical Balanced" },
  { label: "High Yield Fixed Income", value: "High Yield Fixed Income" },
  { label: "Canadian Dividend & Income Equity", value: "Canadian Dividend & Income Equity" },
  { label: "North American Equity", value: "North American Equity" },
  { label: "Multi-Strategy", value: "Multi-Strategy" },
  { label: "Equity Focused", value: "Equity Focused" },
  { label: "Real Estate", value: "Real Estate" },
  { label: "Canadian Small/Mid Cap Equity", value: "Canadian Small/Mid Cap Equity" },
  { label: "Miscellaneous", value: "Miscellaneous" },
];

// Asset Type options based on the database structure
// These are the high-level asset classes that map to sa_asset_class field
// Note: Values are lowercase to match database values for proper filtering
const DEFAULT_ASSET_TYPE_OPTIONS: FilterOption[] = [
  { label: "Equity", value: "equity" },
  { label: "Alternative", value: "alternative" },
  { label: "Fixed Income", value: "fixed_income" },
  { label: "Cash", value: "cash" }
  // We keep only the asset types that return results
];

/**
 * FundsRawFilters Component
 * 
 * Provides filter controls for the Funds Raw Explorer:
 * - Search input (debounced at 300ms)
 * - Fund type filter dropdown
 * - Category filter dropdown (using smart grouping)
 * - Company filter dropdown
 * - Results counter
 */
export function FundsRawFilters({
  searchText,
  onSearchChange,
  selectedCategory,
  selectedFundType,
  selectedSubAssetClass,
  onCategoryFilterChange,
  onFundTypeFilterChange,
  onSubAssetClassFilterChange,
  totalCount,
  visibleCount
}: FundsRawFiltersProps) {
  const [localSearchText, setLocalSearchText] = useState(searchText);
  
  // Use debounced search to reduce API calls
  const debouncedSearchText = useDebounce(localSearchText, 300);
  
  // Sync debounced search text with parent component
  useEffect(() => {
    onSearchChange(debouncedSearchText);
  }, [debouncedSearchText, onSearchChange]);

  // Clear all filters
  const handleClearFilters = () => {
    setLocalSearchText("");
    onSearchChange("");
    onFundTypeFilterChange(null);
    onSubAssetClassFilterChange(null);
  };

  // Calculate active filter count for badge
  const activeFilterCount = [
    (searchText?.trim()?.length || 0) > 0,
    !!selectedFundType,
    !!selectedSubAssetClass
    // Removed selectedCategory from count as we've removed the filter
  ]?.filter(Boolean)?.length || 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Filter dropdowns - Asset Type first, then Category */}
        <Select
          value={selectedFundType || "all"}
          onValueChange={(value) => onFundTypeFilterChange(value === "all" ? null : value)}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Asset Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Asset Types</SelectLabel>
              <SelectItem value="all">All Asset Types</SelectItem>
              {/* Render options from array */}
              {DEFAULT_ASSET_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                  {option.count ? ` (${option.count})` : ""}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        <Select
          value={selectedSubAssetClass || "all"}
          onValueChange={(value) => onSubAssetClassFilterChange(value === "all" ? null : value)}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Categories</SelectLabel>
              <SelectItem value="all">All Categories</SelectItem>
              {/* Render options from array */}
              {DEFAULT_CATEGORY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                  {option.count ? ` (${option.count})` : ""}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        
        {/* Search input */}
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, code, or company..."
            value={localSearchText}
            onChange={(e) => setLocalSearchText(e.target.value)}
            className="pl-9 w-full"
          />
          {localSearchText && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1 h-7 w-7 p-0"
              onClick={() => setLocalSearchText("")}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* Removed Strategies filter as per simplified approach */}
        
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="flex items-center gap-1"
          >
            <X className="h-3.5 w-3.5" />
            Clear Filters
            {activeFilterCount > 1 && (
              <Badge variant="secondary" className="ml-1 text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        )}
      </div>
      
      {/* Results counter */}
      <div className="text-xs text-muted-foreground">
        Showing <span className="font-medium">{(visibleCount || 0).toLocaleString()}</span> of{" "}
        <span className="font-medium">{(totalCount || 0).toLocaleString()}</span> funds
      </div>
    </div>
  );
}

export default FundsRawFilters;